/**
 * "Why is this role ranked here?" — the exact factors behind Most in demand.
 *
 * Every number the comparator looks at is shown, in the order it's applied,
 * with where it came from. If we have no demand evidence for a role we say so
 * and show the neutral placeholder — a career is never pushed to the bottom of
 * a list on the strength of a gap in our data.
 *
 * Display-only. Nothing here scores or feeds the engine.
 */
import { memo, useEffect, useState } from "react";
import { ChevronDown, BarChart3 } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { demandTone } from "@/lib/careerIntel";
import {
  loadDemandIndex,
  NEUTRAL_DEMAND,
  type DemandRank,
} from "@/lib/careerDemandRank";

const SOURCE_LABEL: Record<NonNullable<DemandRank["source"]>, string> = {
  live: "Live market data",
  fallback: "Bundled market snapshot",
  workbook: "Career workbook",
};

function Row({
  step,
  label,
  value,
  note,
}: {
  step: number;
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <li className="flex items-start justify-between gap-3">
      <span className="min-w-0">
        <span className="text-foreground/80">
          <span className="text-muted-foreground">{step}.</span> {label}
        </span>
        {note && <span className="block text-[10px] text-muted-foreground">{note}</span>}
      </span>
      <span className="shrink-0 font-medium tabular-nums text-foreground">{value}</span>
    </li>
  );
}

const DemandBreakdown = memo(function DemandBreakdown({
  ciId,
  name,
}: {
  ciId: string;
  name: string;
}) {
  const [rank, setRank] = useState<DemandRank | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || rank) return;
    let cancelled = false;
    void loadDemandIndex().then((index) => {
      if (!cancelled) setRank(index.get(ciId));
    });
    return () => {
      cancelled = true;
    };
  }, [open, rank, ciId]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="inline-flex min-h-11 items-center gap-1.5 rounded-xl text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label={`How ${name} is ranked for demand`}
        >
          <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" />
          Demand breakdown
          <ChevronDown
            className={`h-3 w-3 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-1.5 rounded-2xl border border-border/50 bg-[hsl(var(--surface-2))] p-3">
          {!rank ? (
            <p className="text-[11px] text-muted-foreground" role="status">
              Pulling the numbers…
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${demandTone(rank.band)}`}
                  aria-hidden="true"
                />
                <p className="text-xs font-semibold">
                  {rank.known ? `${rank.band} demand` : "No demand evidence yet"}
                </p>
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                  {rank.score}/100
                </span>
              </div>

              <ol className="mt-2.5 space-y-2 text-[11px]">
                <Row
                  step={1}
                  label="Market demand band"
                  value={rank.known ? `${rank.band} · ${rank.score}` : `Neutral · ${NEUTRAL_DEMAND}`}
                  note={
                    rank.source
                      ? SOURCE_LABEL[rank.source]
                      : "Held at neutral, never last — we won't guess"
                  }
                />
                <Row
                  step={2}
                  label="Near-term demand · 2026"
                  value={rank.nearTerm != null ? String(rank.nearTerm) : "Not listed"}
                  note="Separates roles sharing the same band"
                />
                <Row
                  step={3}
                  label="Projected growth"
                  value={rank.growthPct != null ? `${Math.round(rank.growthPct)}%` : "Not listed"}
                  note="Applied only when the bands tie"
                />
                <Row
                  step={4}
                  label="Title, then role ID"
                  value="A–Z"
                  note="Keeps the same list in the same order every time"
                />
              </ol>
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

export default DemandBreakdown;
